import React, { useEffect, useRef, useState } from "react";

// Public-domain classical recordings hosted by Wikimedia Commons.
// Users can replace these URLs with their own preferred tracks.
const TRACKS = [
  {
    id: "fur-elise",
    title: "Für Elise (Soft Version)",
    artist: "Beethoven · gentle music-therapy interpretation",
    url: "https://upload.wikimedia.org/wikipedia/commons/1/15/For_Elise_%28F%C3%BCr_Elise%29_Beethoven_JMC_Han.ogg",
  },
  {
    id: "clair-de-lune",
    title: "Clair de Lune",
    artist: "Debussy · serene, heavenly moonlight",
    url: "https://upload.wikimedia.org/wikipedia/commons/b/be/Clair_de_lune_%28Claude_Debussy%29_Suite_bergamasque.ogg",
  },
  {
    id: "ave-maria",
    title: "Ave Maria",
    artist: "Schubert · soft, prayerful lullaby",
    url: "https://upload.wikimedia.org/wikipedia/commons/e/e9/Schubert_Ave_Maria_ukr.oga",
  },
];

export default function MusicPlayer({ position = "bottom-right" }) {
  const [open, setOpen] = useState(false);
  const [playing, setPlaying] = useState(null); // track id
  const audioRef = useRef(null);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  const toggle = (track) => {
    if (playing === track.id) {
      audioRef.current?.pause();
      setPlaying(null);
      return;
    }
    if (audioRef.current) {
      audioRef.current.pause();
    }
    const a = new Audio(track.url);
    a.loop = true;
    a.volume = 0.55;
    a.play().catch(() => {
      // autoplay/CORS failure
    });
    a.addEventListener("ended", () => setPlaying(null));
    a.addEventListener("error", () => setPlaying(null));
    audioRef.current = a;
    setPlaying(track.id);
  };

  const stop = () => {
    audioRef.current?.pause();
    setPlaying(null);
  };

  const posStyle = position === "bottom-right"
    ? { bottom: 28, right: 28 }
    : { bottom: 100, right: 32 };

  return (
    <>
      <button
        data-testid="music-fab"
        onClick={() => setOpen(true)}
        aria-label="Open Serene Music"
        title="Serene Music"
        style={{
          position: "fixed",
          width: 56, height: 56, borderRadius: "50%",
          background: "rgba(15,15,17,0.92)",
          border: "1px solid rgba(212,175,55,0.55)",
          color: playing ? "var(--gold)" : "#dcdce0",
          cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 22, backdropFilter: "blur(8px)", zIndex: 60,
          boxShadow: playing ? "0 0 0 4px rgba(212,175,55,0.18)" : "0 6px 18px rgba(0,0,0,0.4)",
          transition: "all 0.2s ease",
          ...posStyle,
        }}
      >
        {playing ? "♫" : "♪"}
      </button>

      {open && (
        <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 200, display: "flex", alignItems: "flex-end", justifyContent: "center", padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()} className="bg-card" style={{ padding: 32, maxWidth: 520, width: "100%", marginBottom: 24, animation: "fadeIn 0.3s ease" }}>
            <div style={{ textAlign: "center", marginBottom: 6 }}>
              <h2 className="serif" style={{ fontSize: 32, color: "#fff", fontWeight: 400, margin: 0 }}>Serene Music</h2>
              <div className="gold-line" style={{ margin: "12px auto" }} />
              <p className="muted" style={{ fontSize: 14, marginBottom: 24 }}>Soft, gentle company while you remember.</p>
            </div>
            <div style={{ display: "grid", gap: 10 }}>
              {TRACKS.map((t) => {
                const isPlaying = playing === t.id;
                return (
                  <button
                    key={t.id}
                    data-testid={`music-track-${t.id}`}
                    onClick={() => toggle(t)}
                    style={{
                      display: "flex", alignItems: "center", gap: 16,
                      padding: 16, borderRadius: 14, cursor: "pointer", textAlign: "left",
                      background: "var(--bg-3)",
                      border: "1px solid " + (isPlaying ? "var(--gold)" : "var(--border)"),
                      color: "inherit", fontFamily: "inherit",
                      transition: "border-color 0.2s ease",
                    }}
                  >
                    <div style={{
                      width: 44, height: 44, borderRadius: "50%",
                      border: "1.5px solid " + (isPlaying ? "var(--gold)" : "rgba(255,255,255,0.4)"),
                      display: "flex", alignItems: "center", justifyContent: "center",
                      color: isPlaying ? "var(--gold)" : "#dcdce0", fontSize: 16, flexShrink: 0,
                    }}>
                      {isPlaying ? "❚❚" : "▶"}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="serif" style={{ fontSize: 18, color: "#fff", marginBottom: 2 }}>{t.title}</div>
                      <div className="muted" style={{ fontSize: 12, lineHeight: 1.4 }}>{t.artist}</div>
                    </div>
                  </button>
                );
              })}
            </div>
            <div style={{ display: "flex", justifyContent: "center", gap: 24, marginTop: 22 }}>
              {playing && (
                <button data-testid="music-stop" onClick={stop} className="link-gold" style={{ background: "none", border: "none", cursor: "pointer", fontSize: 14, fontFamily: "inherit" }}>
                  Stop
                </button>
              )}
              <button data-testid="music-close" onClick={() => setOpen(false)} className="link-gold" style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, fontFamily: "inherit" }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
